/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AgencyType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface AgencyProps {
  id: string;
  name: string;
  abbreviation: string;
  type: AgencyType;
  isCompetitor: boolean;
  parentCompanyId: string | null;
  notes: string | null;
  isInternallyOwned: boolean;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class AgencyRecord extends Record<AgencyProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  type: null as any,
  isCompetitor: null as any,
  parentCompanyId: null as any,
  notes: null as any,
  isInternallyOwned: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    type: AgencyType;
    isCompetitor: boolean;
    parentCompanyId: string | null;
    notes: string | null;
    isInternallyOwned: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): AgencyRecord {
    if (json == null) {
      throw new Error('AgencyRecord.fromJS: parameter cannot be null');
    }
    return new AgencyRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'AgencyJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'AgencyJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'AgencyJson.abbreviation'),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', AgencyType.Unspecified),
        'AgencyJson.type',
      ),
      isCompetitor: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isCompetitor', false),
        'AgencyJson.isCompetitor',
      ),
      parentCompanyId: json.parentCompanyId,
      notes: json.notes,
      isInternallyOwned: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isInternallyOwned', false),
        'AgencyJson.isInternallyOwned',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    type: AgencyType;
    isCompetitor: boolean;
    parentCompanyId: string | null;
    notes: string | null;
    isInternallyOwned: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [AgencyRecord, Error[]] {
    if (json == null) {
      throw new Error('AgencyRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new AgencyRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'AgencyJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'AgencyJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'AgencyJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', AgencyType.Unspecified),
            'AgencyJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isCompetitor: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isCompetitor', false),
            'AgencyJson.isCompetitor',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      parentCompanyId: tryOrDefault(
        () => json.parentCompanyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isInternallyOwned: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isInternallyOwned', false),
            'AgencyJson.isInternallyOwned',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: AgencyProps) {
    super(props);
    if (props == null) {
      throw new Error('AgencyRecord constructor parameter cannot be null');
    }
  }
}

export abstract class AgencyViewModelBase implements AgencyProps {
  protected readonly _props: AgencyProps;
  private _json?: AgencyProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get type(): AgencyType {
    return this._props.type;
  }
  get isCompetitor(): boolean {
    return this._props.isCompetitor;
  }
  get parentCompanyId(): string | null {
    return this._props.parentCompanyId;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get isInternallyOwned(): boolean {
    return this._props.isInternallyOwned;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAirAmbulance() {
    return this._props.type === AgencyType.AirAmbulance;
  }
  public get isFireService() {
    return this._props.type === AgencyType.FireService;
  }
  public get isGroundAmbulance() {
    return this._props.type === AgencyType.GroundAmbulance;
  }
  public get isOther() {
    return this._props.type === AgencyType.Other;
  }
  public get isPsap() {
    return this._props.type === AgencyType.Psap;
  }
  public get isUnspecified() {
    return this._props.type === AgencyType.Unspecified;
  }

  public isAnyType(...agencyType: AgencyType[]) {
    return agencyType.includes(this._props.type);
  }

  constructor(props: AgencyProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        type: this._props.type,
        isCompetitor: this._props.isCompetitor,
        parentCompanyId: this._props.parentCompanyId,
        notes: this._props.notes,
        isInternallyOwned: this._props.isInternallyOwned,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllAgencyEntities = (state: AppState) => state.entityStores.agency.entities;

export const selectAgencyById = (id: string) => (state: AppState) =>
  state.entityStores.agency.entities.get(id);

export const selectAgencyMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.agency.meta.get(id);

export const selectAgencyAndMetadataById = (id: string) =>
  createSelector(selectAgencyById(id), selectAgencyMetadataById(id), (agency, meta) => ({
    agency,
    meta,
  }));

export function selectAgencyEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllAgencyEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, AgencyRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectAgencyEntitiesByName(name: string) {
  return createSelector(selectAllAgencyEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectAgencyEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllAgencyEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectAgencyEntitiesByType(type: AgencyType) {
  return createSelector(selectAllAgencyEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectAgencyEntitiesByIsCompetitor(isCompetitor: boolean) {
  return createSelector(selectAllAgencyEntities, (entities) =>
    entities.filter((e) => e.isCompetitor === isCompetitor).valueSeq(),
  );
}

export function selectAgencyEntitiesByParentCompanyId(parentCompanyId: string) {
  return createSelector(selectAllAgencyEntities, (entities) =>
    entities.filter((e) => e.parentCompanyId === parentCompanyId).valueSeq(),
  );
}

export function selectAgencyEntitiesByNotes(notes: string) {
  return createSelector(selectAllAgencyEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectAgencyEntitiesByIsInternallyOwned(isInternallyOwned: boolean) {
  return createSelector(selectAllAgencyEntities, (entities) =>
    entities.filter((e) => e.isInternallyOwned === isInternallyOwned).valueSeq(),
  );
}
//#endregion Selectors
